import React, { useEffect, useState } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { LayoutDashboard, Receipt, Upload, Lightbulb, Settings as SettingsIcon, LogOut, Moon, Sun, Wallet, Scale, ChevronRight, Sparkles, Target, BookOpen, Repeat, TrendingUp, Coins, FileWarning, Camera, MoreHorizontal, Compass, HelpCircle } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { toast } from 'sonner';
import { effectivePlan, planMeta, isOnTrial, isBetaActive } from '@/lib/entitlements';
import BetaUiHost from '@/components/BetaUi';
import NotificationBell from '@/components/NotificationBell';
import InviteFriendsButton from '@/components/InviteFriendsButton';
import AppFooter from '@/components/AppFooter';
import BulkImportShellIndicator from '@/components/BulkImportShellIndicator';
import SingleImportShellIndicator from '@/components/SingleImportShellIndicator';
import AnnouncementBanner from '@/components/AnnouncementBanner';
import TrialBanner from '@/components/TrialBanner';
import { ScanReceiptProvider } from '@/context/ScanReceiptContext';
import MobileActionSheet from '@/components/mobile/MobileActionSheet';
import MobileMoreSheet from '@/components/mobile/MobileMoreSheet';
import { supabase } from '@/lib/supabaseClient';
import { getUiVariant } from '@/lib/uiVariant';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const REF_PENDING_KEY  = '_2120_pending_ref';   // sessionStorage — captured at /signup
const REF_RESOLVED_KEY = '_2120_ref_resolved';  // localStorage — set once per user-agent

// =============================================================================
// Sidebar navigation — Phase 39/39.1 (LEGACY) + Phase 1 Figma migration (NEW).
//
// Two configurations coexist:
//   * LEGACY_NAV_GROUPS  → the pre-migration IA (12 items grouped as
//     Daily / Money / Long view / Awareness). Ships in Production until the
//     `ui.new_shell` variant is explicitly enabled.
//   * NEW_NAV_GROUPS     → the redesigned 2120 IA per user-approved Amendment 4
//     (Overview → Transactions → Accounts → Recurring → Plan Ahead → Insights →
//     Reflections). Removes Financial Independence from every UI surface;
//     Goals, Wealth, Liabilities, Net Worth, Projections remain reachable via
//     their existing URLs / the Mobile More sheet, but are not surfaced in the
//     primary sidebar. Import Statement and Settings appear as secondary
//     actions at the bottom of the nav.
//
// Which config renders is decided at render time by `getUiVariant()` from
// `lib/uiVariant.js` (frontend-only, no backend flag).
//
// LEGACY-SHELL FREEZE (Amendment 11, 2026-07-14)
// ----------------------------------------------
// After Phase 1 QA closure the LEGACY_NAV_GROUPS block below and every code
// path reached only when `uiVariant === 'legacy'` is FROZEN. Do not modify
// them except to remediate a confirmed Production regression, and only with
// an explicit commit-message reference to this amendment. All subsequent
// migration work (Phase 2A onward) targets the NEW variant only.
//
// Phase 39.1 legacy notes (retained here for provenance):
//   • Settings removed from the sidebar (still reachable from the profile
//     dropdown + the mobile bottom nav).
//   • Import is a child of Transactions — hidden until /transactions is
//     the active route.
// =============================================================================
const LEGACY_NAV_GROUPS = [
  {
    // No header — the daily trio reads as the primary nav.
    label: null,
    items: [
      { to: '/dashboard',    label: 'Dashboard',    icon: LayoutDashboard, testid: 'nav-dashboard' },
      {
        to: '/transactions',
        label: 'Transactions',
        icon: Receipt,
        testid: 'nav-transactions',
        // children unfold whenever any of these path prefixes is active.
        // Keeps Import discoverable in context without crowding the sidebar.
        children: [
          { to: '/import', label: 'Import', icon: Upload, testid: 'nav-import' },
        ],
        activePathPrefixes: ['/transactions', '/import'],
      },
    ],
  },
  {
    label: 'Money',
    items: [
      { to: '/accounts',    label: 'Accounts',    icon: Wallet,      testid: 'nav-accounts' },
      { to: '/recurring',   label: 'Recurring',   icon: Repeat,      testid: 'nav-recurring' },
      { to: '/net-worth',   label: 'Net Worth',   icon: Scale,       testid: 'nav-net-worth' },
    ],
  },
  {
    label: 'Long view',
    items: [
      { to: '/plan-ahead',  label: 'Plan Ahead',  icon: Compass,     testid: 'nav-plan-ahead' },
    ],
  },
  {
    label: 'Awareness',
    items: [
      { to: '/insights',    label: 'Insights',    icon: Lightbulb, testid: 'nav-insights' },
      { to: '/reflections', label: 'Reflections', icon: BookOpen,  testid: 'nav-reflections' },
    ],
  },
];

// Amendment 4 — approved primary navigation. Seven items, ordered.
// Every item points at a real, protected route in `App.js`.
const NEW_NAV_GROUPS = [
  {
    label: null,
    items: [
      { to: '/dashboard',   label: 'Overview',     icon: LayoutDashboard, testid: 'nav-dashboard' },
      { to: '/transactions', label: 'Transactions', icon: Receipt,        testid: 'nav-transactions' },
      { to: '/accounts',    label: 'Accounts',     icon: Wallet,          testid: 'nav-accounts' },
      { to: '/recurring',   label: 'Recurring',    icon: Repeat,          testid: 'nav-recurring' },
      { to: '/plan-ahead',  label: 'Plan Ahead',   icon: Compass,         testid: 'nav-plan-ahead' },
      { to: '/insights',    label: 'Insights',     icon: Lightbulb,       testid: 'nav-insights' },
      { to: '/reflections', label: 'Reflections',  icon: BookOpen,        testid: 'nav-reflections' },
    ],
  },
];

// Amendment 4 — secondary actions (only rendered in the new variant).
// Positioned below primary nav, above the pricing teaser + user card.
const NEW_SECONDARY_NAV = [
  { to: '/import',   label: 'Import Statement', icon: Upload,        testid: 'nav-import' },
  { to: '/help',     label: 'Help',             icon: HelpCircle,    testid: 'nav-help' },
  { to: '/settings', label: 'Settings',         icon: SettingsIcon,  testid: 'nav-settings' },
];

// Flat list for mobile bottom nav.
// Phase 70E redesign — "Daily 4 + center FAB + More". The centre slot is
// reserved for the camera FAB (rendered separately, not as a NavLink).
// The "More" slot opens a bottom sheet (MobileMoreSheet) instead of
// navigating, so it gets a `kind: 'sheet'` discriminator.
const MOBILE_NAV = [
  { to: '/dashboard',    label: 'Home',        icon: LayoutDashboard, testid: 'nav-dashboard' },
  { to: '/transactions', label: 'Transactions', icon: Receipt,         testid: 'nav-transactions' },
  { kind: 'fab',         label: 'Capture',     icon: Camera,          testid: 'nav-fab' },
  { to: '/recurring', label: 'Recurring', icon: Repeat, testid: 'nav-recurring' },
  { kind: 'more',        label: 'More',        icon: MoreHorizontal,  testid: 'nav-more' },
];

function greet() {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

export default function AppShell({ children }) {
  const { user, profile, signOut } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  // Phase 1 (Figma migration) — pick the sidebar IA per client-side gate.
  // See lib/uiVariant.js for the resolution order (localStorage override →
  // Preview hostname → default 'legacy'). Everything else in this shell
  // renders identically regardless of variant.
  const uiVariant = getUiVariant();
  const navGroups = uiVariant === 'new' ? NEW_NAV_GROUPS : LEGACY_NAV_GROUPS;
  const secondaryNav = uiVariant === 'new' ? NEW_SECONDARY_NAV : [];

  // Phase 70E — mobile bottom-sheet state. Kept locally because nothing
  // outside AppShell needs to drive them.
  const [actionSheetOpen, setActionSheetOpen] = useState(false);
  const [moreSheetOpen, setMoreSheetOpen]     = useState(false);

  // First-name display + first/last initials for the avatar
  const firstNameFromProfile = (profile?.first_name || '').trim();
  const lastNameFromProfile = (profile?.last_name || '').trim();
  const displayName = firstNameFromProfile
    || (profile?.name || profile?.full_name || user?.email || 'there').split(/[\s@]/)[0];
  const initials = (
    (firstNameFromProfile ? firstNameFromProfile[0] : (profile?.name || profile?.full_name || user?.email || '?')[0])
    + (lastNameFromProfile ? lastNameFromProfile[0] : '')
  ).toUpperCase() || '?';

  // Plan label resilience, when profile is briefly unset (e.g. mid-refetch
  // after returning from an external checkout redirect), `effectivePlan(null)`
  // returns 'free'. Showing "Free" in the sidebar at that moment looks like
  // an instant downgrade and has caused real user panic. Hold the label as
  // null while profile is loading; reveal once it lands.
  const plan = effectivePlan(profile);
  const onTrial = isOnTrial(profile);
  const isBeta = isBetaActive(profile);
  const pMeta = planMeta(plan);
  // Phase 45, Beta label takes precedence in the sidebar pill.
  const planLabel = !profile
    ? null
    : (isBeta ? `${pMeta.label} · Beta` : (onTrial ? `${pMeta.label} trial` : pMeta.label));
  const isPaid = plan !== 'free' && !onTrial && !isBeta;

  const handleLogout = async () => {
    await signOut();
    toast.success("Signed out. Take a calm moment whenever you're back.");
    navigate('/login');
  };

  // Phase 71 — Referral resolve. If a /signup?ref=<token> visitor arrived
  // before signing up, the public Signup page wrote `_2120_pending_ref`
  // to sessionStorage. Now that the user is authenticated, claim it once
  // and idempotently. Failures are silent — referral tracking is best-effort
  // and must never block the rest of the app.
  // Phase 84.15 — global keyboard shortcut Cmd/Ctrl+/ opens the
  // Help Center. Mirrors the pattern used by GitHub / Linear / Notion:
  // most users expect "?" or "/" to open help on any app-shell surface.
  useEffect(() => {
    const onKey = (e) => {
      // Skip when the user is typing in an input / textarea / contenteditable.
      const tgt = e.target;
      const tag = (tgt?.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tgt?.isContentEditable) return;
      if ((e.metaKey || e.ctrlKey) && e.key === '/') {
        e.preventDefault();
        navigate('/help');
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navigate]);


  useEffect(() => {
    if (!user) return;
    let ref = null;
    try { ref = window.sessionStorage.getItem(REF_PENDING_KEY) || null; } catch { /* ignore */ }
    if (!ref) return;

    // Per-user-agent guard to avoid retrying on every mount.
    let alreadyTried = false;
    try {
      const flag = window.localStorage.getItem(REF_RESOLVED_KEY);
      alreadyTried = flag === ref;
    } catch { /* ignore */ }
    if (alreadyTried) return;

    (async () => {
      try {
        const { data: s } = await supabase.auth.getSession();
        const token = s?.session?.access_token;
        if (!token) return;
        await axios.post(
          `${BACKEND_URL}/api/invitations/resolve`,
          { token: ref },
          { headers: { Authorization: `Bearer ${token}` } },
        );
        try { window.localStorage.setItem(REF_RESOLVED_KEY, ref); } catch { /* ignore */ }
        try { window.sessionStorage.removeItem(REF_PENDING_KEY); } catch { /* ignore */ }
      } catch {
        // best-effort — don't surface anything to the user
      }
    })();
  }, [user]);

  return (
    <ScanReceiptProvider>
      <div className="min-h-screen bg-background text-foreground">
      {/* Desktop sidebar, dark evergreen (theme-invariant).
          `overflow-hidden` on the aside + scrollable nav guards against
          short laptop viewports (e.g. 14" Windows ~ 720px tall) clipping
          items below the fold. The flex column makes the nav fill what's
          left between the brand block and the user/pricing pinned bottom. */}
      <aside className="hidden md:flex fixed inset-y-0 left-0 w-64 flex-col bg-brand-gradient text-white/90 p-4 shadow-brand-lg overflow-hidden">
        {/* Brand mark + wordmark + Beta tag (Phase 46, private beta period).
            The Beta chip lives on the wordmark, NOT user-conditional, so every
            visitor knows the whole product is in beta. Remove this when we
            cut the public launch. */}
        <div className="flex items-center gap-2.5 px-2 py-3">
          <img src="/brand/logo-mark-light.png" alt="2120" className="h-9 w-9" />
          <img src="/brand/wordmark-gold.png" alt="2120" className="h-6 w-auto" />
          <span
            className="ml-0.5 text-[10px] font-medium tracking-[0.14em] text-white/70 bg-white/10 border border-white/15 rounded-full px-1.5 py-0.5 leading-none lowercase"
            data-testid="brand-beta-tag"
            title="2120 is in private beta"
          >
            beta
          </span>
        </div>
        <div className="text-[11px] text-white/55 px-2 mt-1 mb-3">Financial Clarity For Modern Life.</div>

        <nav className="flex-1 overflow-y-auto -mx-2 px-2 min-h-0 [scrollbar-width:thin] space-y-2" data-testid="sidebar-nav">
          {navGroups.map((group, gi) => (
            <div key={group.label || `group-${gi}`}>
              {group.label && (
                <div
                  className="px-3 pt-1 pb-1.5 text-[10px] uppercase tracking-[0.16em] text-white/40 font-medium select-none"
                  data-testid={`nav-group-${group.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {group.label}
                </div>
              )}
              <div className="space-y-px">
                {group.items.map((item) => {
                  // Path-driven child reveal: when any of `activePathPrefixes`
                  // matches the current pathname, the children unfold under
                  // their parent. No chevron, no extra click — just context.
                  const childrenVisible = item.children && (item.activePathPrefixes || [item.to]).some(
                    (prefix) => location.pathname === prefix || location.pathname.startsWith(`${prefix}/`) || (prefix === item.to && location.pathname === item.to)
                  );
                  return (
                    <React.Fragment key={item.to}>
                      <NavLink
                        to={item.to}
                        data-testid={item.testid}
                        className={({ isActive }) =>
                          `group flex items-center gap-3 rounded-xl px-3 py-2 text-sm transition-all ${
                            isActive
                              ? 'bg-white/12 text-white shadow-[inset_0_0_0_1px_rgba(212,169,93,0.35)]'
                              : 'text-white/70 hover:bg-white/6 hover:text-white'
                          }`
                        }
                      >
                        {({ isActive }) => (
                          <>
                            <item.icon className={`h-4 w-4 ${isActive ? 'text-gold' : 'text-white/80'}`} />
                            <span>{item.label}</span>
                          </>
                        )}
                      </NavLink>
                      {childrenVisible && (
                        <div className="ml-7 mt-px space-y-px border-l border-white/10 pl-2.5" data-testid={`${item.testid}-children`}>
                          {item.children.map((child) => (
                            <NavLink
                              key={child.to}
                              to={child.to}
                              data-testid={child.testid}
                              className={({ isActive }) =>
                                `group flex items-center gap-2 rounded-lg px-2.5 py-1.5 text-[13px] transition-all ${
                                  isActive
                                    ? 'bg-white/10 text-white'
                                    : 'text-white/60 hover:bg-white/6 hover:text-white'
                                }`
                              }
                            >
                              {({ isActive }) => (
                                <>
                                  <child.icon className={`h-3.5 w-3.5 ${isActive ? 'text-gold' : 'text-white/70'}`} />
                                  <span>{child.label}</span>
                                </>
                              )}
                            </NavLink>
                          ))}
                        </div>
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
            </div>
          ))}

          {/* Phase 1 (Figma migration) — secondary actions. Only rendered in
              the new variant; visually separated with a hairline divider.
              Import Statement and Settings live here so the primary nav
              stays focused on the seven approved destinations.
              Phase 83.7 — reduced size + opacity on both items so the
              visual hierarchy makes them clearly subordinate to the
              primary destinations above. Icons de-emphasised to match. */}
          {secondaryNav.length > 0 && (
            <div className="pt-2 mt-2 border-t border-white/10" data-testid="nav-secondary">
              <div className="space-y-px">
                {secondaryNav.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    data-testid={item.testid}
                    className={({ isActive }) =>
                      `group flex items-center gap-3 rounded-xl px-3 py-1.5 text-[13px] transition-all ${
                        isActive
                          ? 'bg-white/10 text-white/90 shadow-[inset_0_0_0_1px_rgba(212,169,93,0.28)]'
                          : 'text-white/50 hover:bg-white/6 hover:text-white/80'
                      }`
                    }
                  >
                    {({ isActive }) => (
                      <>
                        <item.icon className={`h-3.5 w-3.5 ${isActive ? 'text-gold/80' : 'text-white/50'}`} />
                        <span>{item.label}</span>
                      </>
                    )}
                  </NavLink>
                ))}
              </div>
            </div>
          )}
        </nav>

        {/* Pricing teaser, only when not on a paid plan */}
        {!isPaid && (
          <NavLink
            to="/pricing"
            data-testid="nav-pricing"
            className="mb-3 rounded-xl px-3 py-2.5 text-sm flex items-center justify-between gap-2 border border-[hsl(var(--gold))]/35 bg-[hsl(var(--gold))]/12 text-white hover:bg-[hsl(var(--gold))]/18 transition-colors"
          >
            <span className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-gold" /> View plans</span>
            <ChevronRight className="h-3.5 w-3.5 text-gold" />
          </NavLink>
        )}

        {/* Bottom, user card */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button data-testid="sidebar-user-card" className="flex items-center gap-3 rounded-xl p-2 hover:bg-white/6 transition-colors text-left">
              <Avatar className="h-9 w-9 ring-1 ring-white/15">
                <AvatarFallback className="bg-white/10 text-white text-xs">{initials}</AvatarFallback>
              </Avatar>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium truncate text-white">{displayName}</div>
                <div className="text-[11px] text-gold flex items-center gap-1" data-testid="sidebar-plan-label">
                  {planLabel ? <>{planLabel}{profile?.base_currency ? ` · ${String(profile.base_currency).toUpperCase()}` : ''}</> : <span className="opacity-60">Loading…</span>}
                </div>
              </div>
              <ChevronRight className="h-4 w-4 text-white/60" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" side="top" className="w-56">
            <div className="px-2 py-1.5 text-xs text-muted-foreground">{user?.email}</div>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate('/settings')} data-testid="menu-settings">
              <SettingsIcon className="h-4 w-4 mr-2" /> Settings
            </DropdownMenuItem>
            <DropdownMenuItem onClick={handleLogout} data-testid="menu-logout">
              <LogOut className="h-4 w-4 mr-2" /> Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </aside>

      <div className="md:pl-64">
        {/* Global announcement banner (sticky at very top, above header) */}
        <AnnouncementBanner />
        {/* Trial banner, shown while user is on a 14-day free trial */}
        <TrialBanner />
        {/* Top header */}
        <header className="sticky top-0 z-30 backdrop-blur bg-background/70 border-b border-border/60">
          <div className="flex items-center justify-between gap-3 px-4 sm:px-6 lg:px-8 py-3">
            <div className="flex items-center gap-3">
              <div className="md:hidden flex items-center gap-1.5">
                <img src="/brand/logo-mark.png" alt="" className="h-7 w-7 rounded-md" />
                <span
                  className="text-[10px] font-medium tracking-[0.14em] text-muted-foreground bg-muted/60 border border-border/60 rounded-full px-1.5 py-0.5 leading-none lowercase"
                  data-testid="brand-beta-tag-mobile"
                >
                  beta
                </span>
              </div>
              {/* v3.7.19 — When the user is on `/dashboard`, the greeting
                  is owned by `DashboardHeader` (with a subtitle, activation
                  pill and Record-transaction control). Rendering the greeting
                  here as well produced two "Good morning, …" stacks on top of
                  the page. Suppress this compact greeting on the dashboard;
                  other pages continue to show it because they don't own their
                  own header. */}
              {location.pathname !== '/dashboard' && (
                <div data-testid="appshell-greeting">
                  <div className="text-xs text-muted-foreground">{greet()},</div>
                  <div className="font-display text-base font-semibold tracking-tight" data-testid="header-greeting-name">{displayName}</div>
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              <BulkImportShellIndicator />
              <SingleImportShellIndicator />
              <InviteFriendsButton />
              {/* Phase 84.15 \u2014 topbar Help entry point. Icon on desktop
                  + Cmd/Ctrl+/ shortcut (wired below in the effect). */}
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/help')}
                data-testid="topbar-help"
                aria-label="Open Help Center"
                className="rounded-xl"
                title="Help (Cmd/Ctrl+/)"
              >
                <HelpCircle className="h-4 w-4" />
              </Button>
              <NotificationBell />
              <Button variant="ghost" size="icon" onClick={toggle} data-testid="theme-toggle" aria-label="Toggle theme" className="rounded-xl">
                {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" data-testid="user-menu" className="rounded-xl">
                    <Avatar className="h-7 w-7"><AvatarFallback>{initials}</AvatarFallback></Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-2 py-1.5 text-xs text-muted-foreground">{user?.email}</div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/settings')} data-testid="menu-settings-top">
                    <SettingsIcon className="h-4 w-4 mr-2" /> Settings
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleLogout} data-testid="menu-logout-top">
                    <LogOut className="h-4 w-4 mr-2" /> Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        <main className="px-4 sm:px-6 lg:px-8 py-6 pb-24 md:pb-10 fade-in">
          {children}
          <AppFooter />
        </main>

        {/* Phase 45, Private beta: ToS modal + floating feedback FAB. The
            host fetches /api/billing/me to know whether this user is a
            beta tester; until then, only the feedback button renders. */}
        <BetaUiHost />

        {/* Mobile bottom nav — Phase 70E "Daily 4 + Center FAB + More".
            Five grid columns; the third column is a CAMERA FAB that
            opens a bottom action sheet, and the fifth ("More") opens a
            full nav sheet. Everything else is a NavLink, identical to
            the previous behaviour. */}
        <nav
          className="md:hidden fixed bottom-0 inset-x-0 z-30 bg-background/95 backdrop-blur border-t border-border/60 pb-[max(0px,env(safe-area-inset-bottom))]"
          data-testid="mobile-bottom-nav"
        >
          <div className="grid grid-cols-5 items-end">
            {MOBILE_NAV.map((item) => {
              // Centre FAB — elevated, primary-coloured, breaks the row.
              if (item.kind === 'fab') {
                const Icon = item.icon;
                return (
                  <div key="fab-slot" className="flex justify-center">
                    <button
                      type="button"
                      onClick={() => setActionSheetOpen(true)}
                      aria-label="Capture or add"
                      className="-translate-y-3 h-14 w-14 rounded-full bg-primary text-primary-foreground shadow-lg shadow-primary/30 grid place-items-center transition-transform duration-150 active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                      data-testid={`${item.testid}-mobile`}
                    >
                      <Icon className="h-6 w-6" />
                    </button>
                  </div>
                );
              }
              // "More" tab — opens MobileMoreSheet, doesn't navigate.
              if (item.kind === 'more') {
                const Icon = item.icon;
                return (
                  <button
                    type="button"
                    key="more-slot"
                    onClick={() => setMoreSheetOpen(true)}
                    className="flex flex-col items-center gap-1 py-2 text-[10px] text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:text-foreground"
                    data-testid={`${item.testid}-mobile`}
                  >
                    <Icon className="h-5 w-5" />
                    <span>{item.label}</span>
                  </button>
                );
              }
              // Regular nav link.
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  data-testid={`${item.testid}-mobile`}
                  className={({ isActive }) =>
                    `flex flex-col items-center gap-1 py-2 text-[10px] ${isActive ? 'text-primary' : 'text-muted-foreground'}`
                  }
                >
                  <item.icon className="h-5 w-5" />
                  <span>{item.label}</span>
                </NavLink>
              );
            })}
          </div>
        </nav>

        {/* Bottom sheets owned by the shell so they remain available on
            every page without needing to be remounted per route. */}
        <MobileActionSheet open={actionSheetOpen} onOpenChange={setActionSheetOpen} />
        <MobileMoreSheet   open={moreSheetOpen}   onOpenChange={setMoreSheetOpen} />
      </div>
      </div>
    </ScanReceiptProvider>
  );
}
